import { Component, OnInit } from '@angular/core';
import { Property } from 'src/app/model/property';
import { PropertyService } from 'src/app/services/property.service';

@Component({
  selector: 'app-rental-properties',
  templateUrl: './rental-properties.component.html',
  styleUrls: ['./rental-properties.component.css']
})
export class RentalPropertiesComponent implements OnInit {

  // =========================================================
  // PROPERTIES DATA
  // =========================================================
  properties: Property[] = [];
  activeProperties: Property[] = [];

  // =========================================================
  // PAGINATION
  // =========================================================
  displayedProperties: Property[] = [];
  currentPage: number = 1;
  pageSize: number = 12;
  pageStartIndex: number = 0;
  pageEndIndex: number = 0;
  
  // Actual total pages
  totalPages: number[] = [];

  // Pages visible in pagination UI (-1 represents "...")
  visiblePages: number[] = [];

  constructor(
    private propertyService: PropertyService
  ) { }

  // =========================================================
  // INIT
  // =========================================================
  ngOnInit(): void {
    this.loadProperties('rent', 'homeinkl');
  }

  // =========================================================
  // LOAD PROPERTIES
  // =========================================================
  loadProperties(type: string, agentId: string): void {
    this.propertyService
      .getPropertiesByType(type, agentId)
      .subscribe({
        next: (data) => {
          this.properties = data;
          this.activeProperties = this.properties.filter(
            p => p.isActive && p.isActive.trim().toUpperCase() === 'YES'
          );
          this.currentPage = 1;
          this.setupPagination();
        },
        error: (err) => {
          console.error('Error fetching properties:', err);
        }
      });
  }

  // =========================================================
  // SETUP PAGINATION
  // =========================================================
  setupPagination(): void {
    if (!this.activeProperties || this.activeProperties.length === 0) {
      this.displayedProperties = [];
      this.totalPages = [];
      this.visiblePages = [];
      this.pageStartIndex = 0;
      this.pageEndIndex = 0;
      return;
    }

    const totalPageCount = Math.ceil(this.activeProperties.length / this.pageSize);
    this.totalPages = [];

    for (let i = 1; i <= totalPageCount; i++) {
      this.totalPages.push(i);
    }

    this.updateVisiblePages();
    this.updateDisplayedProperties();
  }

  // =========================================================
  // UPDATE VISIBLE PAGINATION
  // =========================================================
  updateVisiblePages(): void {
    const total = this.totalPages.length;
    const current = this.currentPage;
    this.visiblePages = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        this.visiblePages.push(i);
      }
      return;
    }

    if (current <= 4) {
      this.visiblePages = [1, 2, 3, 4, 5, -1, total];
      return;
    }

    if (current >= total - 3) {
      this.visiblePages = [1, -1, total - 4, total - 3, total - 2, total - 1, total];
      return;
    }

    this.visiblePages = [
      1,
      -1,
      current - 2,
      current - 1,
      current,
      current + 1,
      current + 2,
      -1,
      total
    ];
  }

  // =========================================================
  // UPDATE DISPLAYED PROPERTIES
  // =========================================================
  updateDisplayedProperties(): void {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    this.displayedProperties = this.activeProperties.slice(startIndex, endIndex);
    this.pageStartIndex = startIndex;
    this.pageEndIndex = Math.min(endIndex, this.activeProperties.length);
  }

  // =========================================================
  // PAGINATION NAVIGATION
  // =========================================================
  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages.length) {
      return;
    }

    this.currentPage = page;
    this.updateDisplayedProperties();
    this.updateVisiblePages();
    this.scrollToProperties();
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updateDisplayedProperties();
      this.updateVisiblePages();
      this.scrollToProperties();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages.length) {
      this.currentPage++;
      this.updateDisplayedProperties();
      this.updateVisiblePages();
      this.scrollToProperties();
    }
  }

  // =========================================================
  // SCROLL TO PROPERTY SECTION
  // =========================================================
  private scrollToProperties(): void {
    setTimeout(() => {
      const element = document.querySelector('.featured-rentals');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }

  // =========================================================
  // SHARE PROPERTY
  // =========================================================
  shareProperty(event: Event): void {
    event.preventDefault();

    if ((navigator as any).share) {
      (navigator as any).share({
        title: 'Check out this property',
        text: 'Check out this amazing property for rent!',
        url: window.location.href
      }).catch((error: any) => {
        console.error('Error sharing:', error);
      });
    } else {
      alert('Sharing not supported on this browser. You can copy the URL manually.');
    }
  }

}